import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import arrowLeftIcon from "@iconify-icons/lucide/arrow-left";
import { useNavigate } from "react-router-dom";

import AppTopNav from "../layout/header/AppTopNav";
import Footer from "../layout/footer/footer1";

/**
 * /about-us — página informativa. Rediseñada 2026-09 al look
 * Academy: AppTopNav, hero navy compacto y contenido en grid de dos
 * columnas con overline cyan, título + subtítulo y párrafos limpios.
 * Se retira `OurStory1` (queda huérfano) porque su layout Bootstrap
 * (section-area + col-lg-5/7 + heading-bx) chocaba con el hero navy.
 * Copy 100 % preservada de las claves i18n `about.*`.
 */
const About = () => {
  const { t } = useTranslation("global");
  const navigate = useNavigate();

  const stats = [
    { value: "15+", label: t("about.statYears", "años de trayectoria") },
    { value: "30k+", label: t("about.statMembers", "miembros a nivel mundial") },
    { value: "70", label: t("about.statCountries", "países con presencia") },
  ];

  return (
    <>
      <AppTopNav />

      <header
        style={{
          background: "var(--color-ink)",
          color: "#fff",
          padding: "32px 16px 40px",
        }}
      >
        <div className="mx-auto" style={{ maxWidth: 1160 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              minHeight: 40,
              padding: 0,
              border: 0,
              background: "transparent",
              color: "#DCE4F0",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              marginBottom: 8,
            }}
          >
            <Icon icon={arrowLeftIcon} width={18} height={18} aria-hidden="true" />
            {t("common.back", "Volver")}
          </button>
          <h1
            style={{
              fontSize: 32,
              lineHeight: 1.15,
              fontWeight: 800,
              letterSpacing: "-.02em",
              margin: 0,
              /* ACADEMY-34 #6 — `src/css/typography.css:52` de la
                 plantilla vieja fuerza `h1 { color: #111111 }`, que
                 pisa el `color: #fff` heredado del `<header>` (herencia
                 no aplica cuando el hijo tiene color propio definido
                 por regla global). Sobre navy queda ilegible. Color
                 inline garantiza contraste. */
              color: "var(--color-surface)",
            }}
          >
            {t("about.us")}
          </h1>
        </div>
      </header>

      <main className="page-content">
        <div
          className="mx-auto"
          style={{
            maxWidth: 1160,
            padding: "56px 16px 40px",
            display: "grid",
            /* Dos columnas cuando cabe (>=720px útiles), una en
               móvil. `auto-fit` + `minmax(280px, 1fr)` da ese salto
               fluido sin media queries — mismo patrón que /courses. */
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 48,
            alignItems: "start",
          }}
        >
          {/* Col izquierda — introducción */}
          <div>
            <div
              className="text-secondary-strong"
              style={{
                fontSize: 12.5,
                fontWeight: 800,
                letterSpacing: ".12em",
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              {t("about.bigSteps")}
            </div>
            <h2
              style={{
                fontSize: 40,
                lineHeight: 1.1,
                fontWeight: 800,
                letterSpacing: "-.02em",
                margin: 0,
                color: "var(--color-ink)",
              }}
            >
              {t("about.our")}{" "}
              <span style={{ color: "var(--color-accent-text)" }}>
                {t("about.history")}
              </span>
            </h2>
          </div>

          {/* Col derecha — trayectoria + párrafos */}
          <div>
            <h3
              style={{
                fontSize: 22,
                lineHeight: 1.3,
                fontWeight: 800,
                letterSpacing: "-.01em",
                margin: "0 0 20px",
                color: "var(--color-ink)",
              }}
            >
              {t("about.trayectory")}
            </h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                fontSize: 16,
                lineHeight: 1.65,
                fontWeight: 500,
                color: "#3C475E",
              }}
            >
              <p style={{ margin: 0 }}>{t("about.p1")}</p>
              <p style={{ margin: 0 }}>{t("about.p2")}</p>
              <p style={{ margin: 0 }}>{t("about.p3")}</p>
              <p style={{ margin: 0 }}>{t("about.p4")}</p>
            </div>
          </div>
        </div>

        {/* Stats — bloque separador con los tres números */}
        <div
          style={{
            background: "var(--color-bg)",
            borderTop: "1px solid var(--color-line)",
            borderBottom: "1px solid var(--color-line)",
          }}
        >
          <div
            className="mx-auto"
            style={{
              maxWidth: 1160,
              padding: "40px 16px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 24,
            }}
          >
            {stats.map((s) => (
              <div key={s.value} style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: 42,
                    lineHeight: 1.05,
                    fontWeight: 800,
                    letterSpacing: "-.02em",
                    color: "var(--color-accent-text)",
                    marginBottom: 6,
                  }}
                >
                  {s.value}
                </div>
                <div
                  className="text-ink-2"
                  style={{
                    fontSize: 14.5,
                    fontWeight: 700,
                    lineHeight: 1.4,
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer t={t} />
    </>
  );
};

export default About;
